import { apiFetch, v1Path } from './api';
import { parseItems, UUID_RE, WORK_ITEM_KEY_RE } from './work-types';

// ---------------------------------------------------------------------------
// DTOs (docs/specs/wiki.md §6) and guards
// ---------------------------------------------------------------------------

export type WikiSpaceKind = 'org' | 'project';

/** Client-side space handle. `projectId` null = org space. */
export type WikiSpaceRef = { kind: WikiSpaceKind; projectId: string | null };

export type WikiPageRef = { id: string; slug: string; title: string };

export type WikiPageSlim = {
  id: string;
  parent_id: string | null;
  slug: string;
  path: string;
  title: string;
  version: number;
  body_sha256: string;
  updated_at: string;
  updated_by_id: string | null;
  deleted_at: string | null;
};

export type WikiPageFull = WikiPageSlim & {
  body_md: string;
  created_at: string;
  created_by_id: string | null;
  ancestors: WikiPageRef[];
  children: WikiPageRef[];
};

export type WikiRevisionSlim = {
  version: number;
  title: string;
  author_id: string | null;
  created_at: string;
};

export type WikiRevisionFull = WikiRevisionSlim & { body_md: string };

export type WikiSpaceDto = {
  kind: WikiSpaceKind;
  id: string | null;
  identifier: string;
  name: string;
  page_count: number;
  updated_at: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

export function isWikiPageRef(value: unknown): value is WikiPageRef {
  return isRecord(value) && typeof value.id === 'string' && typeof value.slug === 'string' && typeof value.title === 'string';
}

export function isWikiPageSlim(value: unknown): value is WikiPageSlim {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    isNullableString(value.parent_id) &&
    typeof value.slug === 'string' &&
    typeof value.path === 'string' &&
    typeof value.title === 'string' &&
    typeof value.version === 'number' &&
    typeof value.body_sha256 === 'string' &&
    typeof value.updated_at === 'string' &&
    isNullableString(value.updated_by_id) &&
    isNullableString(value.deleted_at)
  );
}

export function isWikiPageFull(value: unknown): value is WikiPageFull {
  return (
    isWikiPageSlim(value) &&
    typeof (value as Record<string, unknown>).body_md === 'string' &&
    typeof (value as Record<string, unknown>).created_at === 'string' &&
    isNullableString((value as Record<string, unknown>).created_by_id) &&
    Array.isArray((value as Record<string, unknown>).ancestors) &&
    ((value as Record<string, unknown>).ancestors as unknown[]).every(isWikiPageRef) &&
    Array.isArray((value as Record<string, unknown>).children) &&
    ((value as Record<string, unknown>).children as unknown[]).every(isWikiPageRef)
  );
}

export function isWikiRevisionSlim(value: unknown): value is WikiRevisionSlim {
  return (
    isRecord(value) &&
    typeof value.version === 'number' &&
    typeof value.title === 'string' &&
    isNullableString(value.author_id) &&
    typeof value.created_at === 'string'
  );
}

export function isWikiRevisionFull(value: unknown): value is WikiRevisionFull {
  return isWikiRevisionSlim(value) && typeof (value as Record<string, unknown>).body_md === 'string';
}

export function isWikiSpaceDto(value: unknown): value is WikiSpaceDto {
  return (
    isRecord(value) &&
    (value.kind === 'org' || value.kind === 'project') &&
    isNullableString(value.id) &&
    typeof value.identifier === 'string' &&
    typeof value.name === 'string' &&
    typeof value.page_count === 'number' &&
    isNullableString(value.updated_at)
  );
}

function parseOne<T>(value: unknown, guard: (item: unknown) => item is T, context: string): T {
  if (!guard(value)) throw new Error(`Malformed ${context} response.`);
  return value;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export function wikiApiBase(space: WikiSpaceRef): string {
  return space.kind === 'project' && space.projectId
    ? v1Path(`/projects/${space.projectId}/wiki`)
    : v1Path('/wiki');
}

export function wikiRoute(space: WikiSpaceRef, path?: string): string {
  const root = space.kind === 'project' && space.projectId ? `/wiki/projects/${space.projectId}` : '/wiki/org';
  return path ? `${root}/${path}` : root;
}

export type WikiLocation = { space: WikiSpaceRef; path: string };

/** Inverse of `wikiRoute`: the space and page path addressed by a pathname, or null outside a space. */
export function parseWikiLocation(pathname: string): WikiLocation | null {
  const org = pathname.match(/^\/wiki\/org(?:\/(.*))?$/);
  if (org) return { space: { kind: 'org', projectId: null }, path: (org[1] ?? '').replace(/\/+$/, '') };
  const project = pathname.match(/^\/wiki\/projects\/([^/]+)(?:\/(.*))?$/);
  if (project && project[1] && UUID_RE.test(project[1])) {
    return { space: { kind: 'project', projectId: project[1] }, path: (project[2] ?? '').replace(/\/+$/, '') };
  }
  return null;
}

export async function listWikiSpaces(): Promise<WikiSpaceDto[]> {
  return parseItems(await apiFetch<unknown>(v1Path('/wiki/spaces')), isWikiSpaceDto, 'wiki spaces');
}

export async function listWikiPages(space: WikiSpaceRef, q?: string): Promise<WikiPageSlim[]> {
  const suffix = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  return parseItems(await apiFetch<unknown>(`${wikiApiBase(space)}/pages${suffix}`), isWikiPageSlim, 'wiki pages');
}

export async function getWikiPage(space: WikiSpaceRef, pageId: string): Promise<WikiPageFull> {
  return parseOne(await apiFetch<unknown>(`${wikiApiBase(space)}/pages/${pageId}`), isWikiPageFull, 'wiki page');
}

export async function getWikiPageByPath(space: WikiSpaceRef, path: string): Promise<WikiPageFull> {
  const encoded = path.split('/').map(encodeURIComponent).join('/');
  return parseOne(
    await apiFetch<unknown>(`${wikiApiBase(space)}/pages/by-path/${encoded}`),
    isWikiPageFull,
    'wiki page',
  );
}

export async function createWikiPage(
  space: WikiSpaceRef,
  body: { parent_id: string | null; slug: string; title: string; body_md?: string },
): Promise<WikiPageFull> {
  return parseOne(
    await apiFetch<unknown>(`${wikiApiBase(space)}/pages`, { method: 'POST', body: JSON.stringify(body) }),
    isWikiPageFull,
    'wiki page',
  );
}

export async function patchWikiPage(
  space: WikiSpaceRef,
  pageId: string,
  body: { version: number; parent_id?: string | null; slug?: string; title?: string; body_md?: string },
): Promise<WikiPageFull> {
  return parseOne(
    await apiFetch<unknown>(`${wikiApiBase(space)}/pages/${pageId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    isWikiPageFull,
    'wiki page',
  );
}

export async function deleteWikiPage(space: WikiSpaceRef, pageId: string): Promise<void> {
  await apiFetch(`${wikiApiBase(space)}/pages/${pageId}`, { method: 'DELETE' });
}

export async function listWikiRevisions(space: WikiSpaceRef, pageId: string): Promise<WikiRevisionSlim[]> {
  return parseItems(
    await apiFetch<unknown>(`${wikiApiBase(space)}/pages/${pageId}/revisions`),
    isWikiRevisionSlim,
    'wiki revisions',
  );
}

export async function getWikiRevision(space: WikiSpaceRef, pageId: string, version: number): Promise<WikiRevisionFull> {
  return parseOne(
    await apiFetch<unknown>(`${wikiApiBase(space)}/pages/${pageId}/revisions/${version}`),
    isWikiRevisionFull,
    'wiki revision',
  );
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export const WIKI_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Title → slug per wiki.md W-4. Returns '' when nothing usable remains. */
export function slugify(title: string): string {
  return title
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '');
}

const LEADING_ATX_H1 = /^[ \t]*#[ \t]+(.+?)[ \t]*#*[ \t]*$/;

/**
 * Drop a leading ATX H1 from rendered Markdown when it repeats the page title.
 * Stored source is unchanged (W-5).
 */
export function bodyWithoutMatchingTitle(body: string, title: string): string {
  const expected = title.trim();
  if (expected.length === 0) return body;
  const lineEnd = body.search(/\r?\n/);
  const firstLine = lineEnd === -1 ? body : body.slice(0, lineEnd);
  const match = LEADING_ATX_H1.exec(firstLine);
  if (!match) return body;
  if (match[1].trim().localeCompare(expected, undefined, { sensitivity: 'base' }) !== 0) return body;
  if (lineEnd === -1) return '';
  return body.slice(lineEnd).replace(/^\r?\n(?:[ \t]*\r?\n)?/, '');
}

export type WikiTreeNode = { page: WikiPageSlim; children: WikiTreeNode[] };

/** Nest a flat page list by parent_id. Orphans (parent not in list) become roots. Siblings sort by title. */
export function buildWikiTree(pages: WikiPageSlim[]): WikiTreeNode[] {
  const ids = new Set(pages.map((page) => page.id));
  const byParent = new Map<string | null, WikiPageSlim[]>();
  for (const page of pages) {
    const key = page.parent_id && ids.has(page.parent_id) ? page.parent_id : null;
    const list = byParent.get(key) ?? [];
    list.push(page);
    byParent.set(key, list);
  }
  const compare = (a: WikiPageSlim, b: WikiPageSlim) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  function build(parentId: string | null): WikiTreeNode[] {
    return (byParent.get(parentId) ?? [])
      .slice()
      .sort(compare)
      .map((page) => ({ page, children: build(page.id) }));
  }
  return build(null);
}

/** Ids of `pageId` and everything below it. */
export function descendantIds(pages: WikiPageSlim[], pageId: string): Set<string> {
  const byParent = new Map<string | null, WikiPageSlim[]>();
  for (const page of pages) {
    const list = byParent.get(page.parent_id) ?? [];
    list.push(page);
    byParent.set(page.parent_id, list);
  }
  const out = new Set<string>([pageId]);
  const stack = [pageId];
  while (stack.length) {
    const current = stack.pop()!;
    for (const child of byParent.get(current) ?? []) {
      if (!out.has(child.id)) {
        out.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return out;
}

/**
 * Work-item key from a `/browse/<KEY>` href, else null. Relative hrefs always qualify;
 * absolute ones only when `origin` is given and matches. Used to upgrade plain
 * Markdown links into live cards (wiki.md W-10).
 */
export function workItemKeyFromHref(href: string, origin?: string): string | null {
  if (typeof href !== 'string' || href.length === 0) return null;
  let pathname = href;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) {
    if (!origin) return null;
    try {
      const url = new URL(href);
      if (url.origin !== origin) return null;
      pathname = url.pathname;
    } catch {
      return null;
    }
  }
  const match = pathname.match(/^\/browse\/([^/?#]+)\/?$/);
  if (!match) return null;
  let key: string;
  try {
    key = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  return WORK_ITEM_KEY_RE.test(key) ? key.toUpperCase() : null;
}

/**
 * A block that is exactly one link `[KEY](/browse/KEY)` on its own line is a work-item
 * embed (wiki.md W-10). Returns the matched raw text and key, or null so marked's
 * normal paragraph tokenizer runs. `src` starts at the block being tokenized.
 */
export function matchEmbedBlock(src: string): { raw: string; key: string } | null {
  const match = src.match(/^\[([^\]\n]+)\]\((\/browse\/[^)\s]+)\)[ \t]*(?:\n+|$)/);
  if (!match) return null;
  const key = workItemKeyFromHref(match[2]);
  if (!key || match[1].trim().toUpperCase() !== key) return null;
  return { raw: match[0], key };
}

export function browseHrefForKey(key: string): string {
  if (!WORK_ITEM_KEY_RE.test(key)) {
    throw new Error('Invalid work item key.');
  }
  return `/browse/${key.toUpperCase()}`;
}

// ---------------------------------------------------------------------------
// Local drafts (wiki.md W-8)
// ---------------------------------------------------------------------------

export type WikiDraft = { title: string; body_md: string; saved_at: string };

function draftKey(pageId: string, version: number): string {
  return `bb_wiki_draft:${pageId}:${version}`;
}

function isWikiDraft(value: unknown): value is WikiDraft {
  return (
    isRecord(value) &&
    typeof value.title === 'string' &&
    typeof value.body_md === 'string' &&
    typeof value.saved_at === 'string'
  );
}

export function loadWikiDraft(pageId: string, version: number): WikiDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(pageId, version));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isWikiDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveWikiDraft(pageId: string, version: number, draft: Omit<WikiDraft, 'saved_at'>): WikiDraft {
  const full: WikiDraft = { ...draft, saved_at: new Date().toISOString() };
  try {
    localStorage.setItem(draftKey(pageId, version), JSON.stringify(full));
  } catch {
    // Storage full or unavailable: the in-memory editor state is still the source of truth.
  }
  return full;
}

export function clearWikiDraft(pageId: string, version: number): void {
  try {
    localStorage.removeItem(draftKey(pageId, version));
  } catch {
    return;
  }
}
